
import { Component, ElementRef, HostBinding, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Optional, Self, ViewChild } from "@angular/core";
import { ControlValueAccessor, NgControl } from "@angular/forms";
import { MatFormFieldControl } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { Country } from "iso-3166-1/dist/iso-3166";
import { Subject } from "rxjs";
import { all as allCountries } from "iso-3166-1";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { coerceBooleanProperty } from "@angular/cdk/coercion";

@Component({
  selector: 'select-country',
  templateUrl: './select-country.component.html',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SelectCountryComponent,
      multi: true
    }
  ]
})
export class SelectCountryComponent implements MatFormFieldControl<Country>, OnDestroy, OnInit, ControlValueAccessor {
  
  static nextId = 0;
    
  @Input("formControlName") control: string;
  
  @HostBinding() id = `select-country-${SelectCountryComponent.nextId++}`;
  @ViewChild("input") input: ElementRef;
  @ViewChild(MatInput) matInput: MatInput;
    
  options: Country[];
  filteredOptions: Country[];
  stateChanges = new Subject<void>();
  onChange: (_: Country | null) => void = (country: Country | null) => {};
  onTouched: any;
  _placeholder: string = $localize `Start typing`;
  _value: Country | null;
  focused = false;
  disabled: boolean;
  _required = false;
  touched = false;
  autofilled = false;
  controlType = "select-country";
  
  userAriaDescribedBy = undefined; //TODO
  disableAutomaticLabeling: true; //TODO
  
  constructor(
    @Inject(LOCALE_ID) private locale: string,
    @Optional() @Self() public ngControl: NgControl
    ) {
      
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
    
    this.options = this.buildOptions();
  }
  
  ngOnInit(): void {
  }
  
  private buildOptions(): Country[] {
    
    const allSupportedCountries = allCountries();
    const regionNames = new Intl.DisplayNames([this.locale], { type: 'region' });

    return allSupportedCountries.map(country => {
        
        const localized = regionNames.of(country.alpha2);
        if (localized) {
          country.country = localized;
        }

        return country;
    });
  }
  
  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = null;
    this.filteredOptions = this._filter(input.value);
  }
  
  writeValue(country: Country | null): void {
    this.value = country;
  }
  
  registerOnChange(fn: (_: Country | null) => void): void {
    this.onChange = fn;
  }
  
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  
  @Input()
  get required() {
    return this._required;
  }
  
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
    
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  
  set value(country: Country | null) {
    this._value = country;
    this.onChange(country);
    this.stateChanges.next();
  }
  
  get value(): Country | null {
    return this._value;
  }
  
  onAutocompleteSelection(event: MatAutocompleteSelectedEvent): void {
    this.value = event.option.value;
  }
  
  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  
  set placeholder(plh: string) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  
  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this.input.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }
  
  displayFn(country: Country | null): string {
    return country ? country.country : '';
  }
  
  private _filter(country: string): Country[] {
  
    const filterValue = country.toLowerCase();

    return this.options.filter(option => option.country.toLowerCase().includes(filterValue));
  }
  
  get empty() {
    return !this.value;
  }
  
  ngOnDestroy() {
    this.stateChanges.complete();
  }
  
  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }
  
  get errorState(): boolean {
    return this.required && !this.value && this.touched;
  }
  
  setDescribedByIds(ids: string[]): void {
    //TODO
  }
    
  onContainerClick(event: MouseEvent): void {
    this.matInput.focus();
  }
}
